import classNames from "classnames";
import * as React from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import style from "./user-menu.scss";
import { DropdownMenu, DropdownMenuDivider } from "components/dropdown-menu/DropdownMenu";
import ToggleablePanel from "components/header/ToggleablePanel";
import Chevron from "components/icons/Chevron";
import LanguageMenu from "components/language-menu/LanguageMenu";
import CreateNewPasswordView from "components/login/create-new-password/CreateNewPasswordView";
import { ErrorModal } from "components/login/ErrorModal";
import MenuItemButton from "components/menu-item-button/MenuItemButton";
import Modal from "components/modal/Modal";
import { NavigationMenuItem } from "components/navigation/navigation-menu-item/NavigationMenuItem";
import { PROFILE_ROUTE, TENANT_PROFILE_ROUTE } from "components/router/Routes";
import SettingsView from "components/settings/SettingsView";
import { Breakpoint } from "domain/breakpoints";
import { featureFlagService, FLAG_RESTRUCTURED_SETTINGS } from "services/feature/FeatureFlagService";
import { Action, Category, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import { hasTenantCookie } from "services/tenants/tenantCookieService";
import { userSessionService } from "services/user/UserSessionService";
import { setMobileMainNavigationOpen } from "store/mobileNavigation";
import { resolveColor } from "utils/commonFunctions";

import testIds from "testIds.json";

interface Props {
    className?: string;
    onSubNavigationOpenChange?: (value: boolean) => void;
}

export const UserMenu = (props: Props): JSX.Element => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [settingsVisibility, setSettingsVisibility] = useState(false);
    const [changePasswordFormVisibility, setChangePasswordFormVisibility] = useState(false);
    const [errorModalVisible, setErrorModalVisible] = React.useState<boolean>(false);
    const currentUser = userSessionService.currentUserDetails();

    const isRestructuredSettingsFeatureFlagActive = featureFlagService.isFeatureEnabled(FLAG_RESTRUCTURED_SETTINGS);

    // Open settings modal when URL contains #unsubscribe hash (for email unsubscribe links)
    React.useEffect(() => {
        if (window.location.hash === "#unsubscribe") {
            setSettingsVisibility(true);
            window.history.replaceState(null, "", window.location.pathname);
        }
    }, []);

    const showChangePasswordForm = () => {
        setChangePasswordFormVisibility(true);
    };
    const hideChangePassword = () => {
        setChangePasswordFormVisibility(false);
    };

    const showSettings = () => {
        usageStatisticsService.sendEvent({
            category: Category.HEADER,
            action: Action.VIEW_USER_SETTINGS,
        });
        setSettingsVisibility(true);
    };

    const hideSettings = () => {
        setSettingsVisibility(false);
    };

    const isChangePasswordAllowed = () => {
        const loginMethod = currentUser?.loginMethod || currentUser?.tenantLoginMethod;
        return !loginMethod || loginMethod === "EMAIL" || loginMethod === "COMPANY_ID_AND_EMAIL";
    };

    const profileRoute = hasTenantCookie() ? TENANT_PROFILE_ROUTE : PROFILE_ROUTE;

    const handleProfileLinkClick = () => {
        props.onSubNavigationOpenChange?.(false);
        if (window.innerWidth <= Breakpoint.MD) {
            dispatch(setMobileMainNavigationOpen(false));
        }
    };

    const menuItems: JSX.Element[] = [
        <div key="0" className={style.versionInformation}>
            <strong>Blancco Management Portal</strong>
            <br />
            {t("Common.version")} {process.env.BMP_VERSION}
        </div>,
        <DropdownMenuDivider key="1" />,
        <NavigationMenuItem
            className={style.userMenuItem}
            as="link"
            to={profileRoute.path}
            testId={testIds.navigation.userMenu.profileLink}
            key="2"
            onClick={handleProfileLinkClick}
        >
            {t(profileRoute.title)}
        </NavigationMenuItem>,
        <React.Fragment key="3">
            {!isRestructuredSettingsFeatureFlagActive && (
                <MenuItemButton
                    className={style.userMenuItem}
                    tabIndex={0}
                    onClick={showSettings}
                    data-testid={testIds.navigation.userMenu.settingLink}
                >
                    {t("Common.settings")}
                </MenuItemButton>
            )}
        </React.Fragment>,
        <>
            {!hasTenantCookie() && isChangePasswordAllowed() && (
                <MenuItemButton
                    className={style.userMenuItem}
                    key="4"
                    tabIndex={0}
                    onClick={showChangePasswordForm}
                    data-testid={testIds.navigation.userMenu.changePasswordLink}
                >
                    {t("Routes.changePassword")}
                </MenuItemButton>
            )}
        </>,
        <ToggleablePanel
            key={5}
            title={t("Common.language")}
            testId={testIds.navigation.userMenu.languageMenu.button}
            menuType={"HEADER"}
            chevronSize={{ width: 14, height: 14 }}
            buttonClass={style.navButton}
            customToggleIcon={
                <Chevron
                    color={resolveColor("--app-userMenu-linkColor")}
                    className={style.chevronRight}
                    width={16}
                    height={16}
                />
            }
            hideChevron
        >
            <>
                <LanguageMenu className={style.flyoutLanguageMenu} />
            </>
        </ToggleablePanel>,
        <DropdownMenuDivider key="6" />,

        <MenuItemButton
            className={style.userMenuItem}
            key="7"
            tag="a"
            target="_blank"
            tabIndex={0}
            href="https://status.blancco.com"
            data-testid={testIds.navigation.userMenu.systemStatusLink}
            onClick={() => {
                usageStatisticsService.sendEvent({
                    category: Category.HEADER,
                    action: Action.VISIT_SYSTEM_STATUS,
                });
            }}
        >
            {t("Routes.systemStatus")}
        </MenuItemButton>,
        <MenuItemButton
            className={style.userMenuItem}
            key="8"
            tag="a"
            target="_blank"
            tabIndex={0}
            href="https://trust.blancco.com"
            data-testid={testIds.navigation.userMenu.trustCenterLink}
            onClick={() => {
                usageStatisticsService.sendEvent({
                    category: Category.HEADER,
                    action: Action.VISIT_TRUST_CENTER,
                });
            }}
        >
            {t("Routes.trustCenter")}
        </MenuItemButton>,
        <DropdownMenuDivider key="9" />,

        <MenuItemButton
            className={style.userMenuItem}
            key="10"
            tag="a"
            tabIndex={0}
            href="/logout"
            data-testid={testIds.navigation.userMenu.logoutLink}
            onClick={() => {
                usageStatisticsService.sendEvent({
                    category: Category.HEADER,
                    action: Action.LOGOUT,
                });
            }}
        >
            {t("Routes.logout")}
        </MenuItemButton>,
    ];

    return (
        <>
            <DropdownMenu
                className={classNames(style.menu, props.className)}
                menuItems={menuItems}
                testId={testIds.navigation.userMenu.itself}
            />
            <Modal isOpen={settingsVisibility} hideModal={hideSettings} modalTitle={t("Common.settings")}>
                <SettingsView onCancel={hideSettings} />
            </Modal>
            <Modal
                isOpen={changePasswordFormVisibility}
                hideModal={hideChangePassword}
                modalTitle={t("Routes.changePassword")}
            >
                <CreateNewPasswordView
                    changePassword={true}
                    setChangePasswordFormVisibility={hideChangePassword}
                    errorModalVisible={setErrorModalVisible}
                />
            </Modal>
            {errorModalVisible && (
                <ErrorModal
                    hide={() => setErrorModalVisible(false)}
                    visible={errorModalVisible}
                    title={t("CreateNewPasswordView.attemptsExceededErrorTitle")}
                    description={t("CreateNewPasswordView.attemptsExceededErrorMessage")}
                />
            )}
        </>
    );
};
